/**
 * Multi-root GM entity list — #132 (Phase 1 GM UX completion, Wave 2).
 *
 * Replaces the single-root Locations list (which filtered to
 * `e.root === 'location'`) with a six-section accordion: one collapsible
 * section per `EntityRoot` (`location`, `character`, `faction`, `event`,
 * `item`, `lore`), each carrying a count badge and per-root empty
 * state copy.
 *
 * ## Layout choice — accordion (not tabs)
 *
 * The ticket leaves the variant open ("collapsible headers vs tab
 * strip"). We picked native `<details>/<summary>` accordion for four
 * reasons:
 *
 *   1. No new model state. Tabs would require an `activeRoot` field in
 *      Model, a new Msg, and tear-down/setup churn on every switch.
 *      `<details>` carries its own `open` attribute and is forgotten on
 *      navigation away — fine, since the ticket doesn't ask us to
 *      persist the open-set.
 *   2. Better cap behaviour. With ~50 entities × 6 roots = ~300 rows,
 *      a tab strip hides 5/6 of the content. Accordion lets the GM keep
 *      Characters open while leaving Lore collapsed — closer to the
 *      "scrolling regret" floor the ticket calls out.
 *   3. Mobile parity is free. The ticket asks for "single accordion on
 *      <480px"; the same element answers desktop and phone.
 *   4. A11y is free. `<details>/<summary>` carries native expanded/
 *      collapsed semantics, Enter/Space toggle, focus visible — no
 *      ARIA we have to maintain.
 *
 * ## Sort
 *
 * Inside each root we sort by `normalizeName(displayName)` — the same
 * NFD-fold the name lane (#39) uses — so `Café` and `Cafe` land next
 * to each other regardless of how the GM typed them. `localeCompare`
 * on the normalised key gives a stable, locale-aware ordering.
 *
 * ## Non-color signal (#86)
 *
 * Each root header pairs a color chip (CSS background via
 * `[data-root="<root>"]`) with the canonical Lucide per-root icon
 * (`@dl/ui` `entityIcon`, §5) — color is not the only signal, and the
 * icon is inline SVG so there is no emoji-font dependency. The icon is
 * `aria-hidden` because the section already has the localised root
 * label in its accessible name.
 *
 * ## llui plugin shape
 *
 * Every reactive accessor in this view closes over `entitiesAccessor`,
 * a `(s) => Record<string, EntityState>` same-module wrapper that the
 * consumer module declares against its own Model. The wrapper is
 * passed as a regular function argument; the v2c walker resolves it
 * because the consumer declares it as a same-module `const` (NOT an
 * arrow on the call site — that would re-introduce the
 * higher-order-parameter opacity flagged by `llui/opaque-state-flow`).
 *
 * See `apps/client/README.md` "Higher-order function parameters" for
 * the full pattern.
 */
import { normalizeName } from '@dl/search';
import {
  BUILTIN_NAME_PREDICATE_ID,
  currentFactValue,
  hasObjectUniquenessKey,
  splitFactKey,
  type EntityRoot,
  type EntityState,
} from '@dl/shared';
import {
  badge,
  button as uiButton,
  collapse,
  emptyState,
  entityCard,
  entityIcon,
  plusIcon,
} from '@dl/ui';
import {
  div,
  each,
  show,
  span,
  text,
  ul,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

import { formatNumberValueForDisplay, loadGmRegistry, predicateLabel } from './fact-row-editor.js';

export interface EntityListNav {
  /** Resolve the href for an entity link (router.href). */
  href: (id: string) => string;
  /** Build the navigate Msg the consumer should dispatch on click. */
  navigateMsg: (id: string) => unknown;
  /**
   * #N002 — build the Msg dispatched when the GM clicks the per-section
   * "+ New <root>" button. The list view doesn't know the consumer's
   * Msg shape, so the caller wires the creation flow.
   */
  createForRootMsg: (root: EntityRoot) => unknown;
}

/**
 * The model contract — anything carrying a `Record<string,
 * EntityState>` of entities can render the multi-root list. The
 * `entitiesField` accessor below extracts that bag from Model. Keeping
 * it as a named accessor (rather than `s.entities` inline) lets the
 * consumer thread a different field name (e.g. a snapshot subset)
 * without forking this view.
 */
export type EntitiesBag = Record<string, EntityState>;

/**
 * Structural model contract — the host provides `entities` directly.
 * Lifting to a `<Model extends EntityListStateShape>` constraint (rather
 * than an `(s) => EntitiesBag` accessor parameter) eliminates the
 * function-parameter opacity the v0.5.x cross-file walker can't trace.
 * Same pattern as `switcher-state.ts` — bare-Identifier function
 * declaration the analyzer resolves statically.
 */
export interface EntityListStateShape {
  entities: EntitiesBag;
  /**
   * Resolved asset signed URLs (#13 list thumbnails). Keyed by asset id;
   * populated by the app's asset-url effect (which already scans every
   * entity's asset facts, so list-entity thumbs resolve too). Absent entry
   * = URL not resolved yet → the row's `thumbUrl` is '' and CSS hides it.
   */
  assetUrls: Record<string, string>;
}

/** Bare-Identifier accessor — same-module function declaration. */
export function pickEntities<S extends EntityListStateShape>(s: S): EntitiesBag {
  return s.entities;
}

/** Bare-Identifier accessor — rows for one root from the state. */
export function pickRowsForRoot<S extends EntityListStateShape>(
  s: S,
  root: EntityRoot,
): EntityRow[] {
  return rowsForRoot(s.entities, root);
}

/**
 * Bare-Identifier accessor — count of non-soft-deleted entities at one
 * root. Does NOT go through `pickRowsForRoot` because that path calls
 * the cross-file `normalizeName` (sortKey building), which the v0.5.x
 * walker cannot trace into — and tainting `count` opacifies every
 * reactive accessor that calls it.
 */
export function pickCountForRoot<S extends EntityListStateShape>(
  s: S,
  root: EntityRoot,
): number {
  let n = 0;
  for (const entity of Object.values(s.entities)) {
    if (entity.root !== root) continue;
    if (entity.soft_deleted) continue;
    n++;
  }
  return n;
}

const ENTITY_ROOTS: readonly EntityRoot[] = [
  'location',
  'character',
  'faction',
  'event',
  'item',
  'lore',
];

interface EntityRow {
  id: string;
  name: string;
  sortKey: string;
  /**
   * The mono meta line for the `entityCard` (mock `.result-meta`). Starts
   * with the localised root label, then the curated `featured`-predicate
   * summary (#13) when present — e.g. "Location · Population: 12000 ·
   * Government: monarchy". Always non-empty (the root label is the floor).
   */
  meta: string;
}

/** Format a primitive fact value for the compact list summary. */
function formatSummaryValue(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return null; // bare yes/no reads as noise in a list row
  if (typeof v === 'object') {
    // Dimensioned number value (ADR-0007): render back into its unit + symbol.
    return formatNumberValueForDisplay(v);
  }
  const s = String(v).trim();
  return s === '' ? null : s;
}

/**
 * Curated `featured`-predicate summary for an entity (#13,
 * `30-design-system.md §7.2`) — predicates declared `featured: true` in the
 * registry, promoted into the list row. Singular slot only. Joined as
 * "Label: value" segments.
 */
function featuredSummary(entity: EntityState): string {
  const registry = loadGmRegistry();
  const parts: string[] = [];
  for (const [key, fact] of Object.entries(entity.facts)) {
    const { predicate, instance } = splitFactKey(key);
    if (instance !== null) continue;
    if (!registry.lookup(predicate)?.featured) continue;
    const text = formatSummaryValue(currentFactValue(fact));
    if (text === null) continue;
    parts.push(`${predicateLabel(predicate)}: ${text}`);
  }
  return parts.join(' · ');
}

/** Max characters a short-scalar descriptor value may carry (longer reads as prose noise). */
const DESCRIPTOR_MAX_LEN = 28;
/** How many fallback descriptors to surface on a list/palette row. */
const DESCRIPTOR_MAX_COUNT = 2;

/**
 * Cheap, data-driven row DESCRIPTOR for the entity-card meta line (#B,
 * `30-design-system.md §7.2`). When no predicate is declared `featured`
 * (the common case today — the core pack flags none), the meta line would
 * otherwise read just the root label ("Location"). This lifts that floor by
 * promoting up to two SHORT SCALAR facts the entity already carries —
 * `enum` reads as a clean subtype ("secure", "active", "loyal"), short
 * `text` / `number` as a key descriptor ("41000", "King of the Marches").
 *
 * It is deliberately conservative — refs (bare entity ids read as noise),
 * prose (`rich_text`), assets, dates/times, booleans, multi-cardinality and
 * instanced facts are all skipped — and works purely off the already-folded
 * `entity.facts` + the in-memory registry (no scan beyond this entity, no
 * network), so it stays cheap enough for per-keystroke palette rendering.
 *
 * Values only (no "Label:" prefix): the mock's `Location · city · capital`
 * treatment reads the descriptors as bare tags after the root, not as a
 * key-value grid (that lives in the infobox). `enum` values are preferred
 * over free text so the cleanest subtype-like signal wins the limited slots.
 */
function scalarDescriptor(entity: EntityState): string {
  const registry = loadGmRegistry();
  const enumParts: string[] = [];
  const textParts: string[] = [];
  for (const [key, fact] of Object.entries(entity.facts)) {
    const { predicate, instance } = splitFactKey(key);
    if (instance !== null) continue;
    if (predicate === FACT_NAME_PREDICATE) continue; // the name is the title, not a descriptor
    const decl = registry.lookup(predicate);
    if (!decl) continue;
    // Multi (object-keyed) predicates store arrays — they read poorly
    // compressed into a single inline tag; the infobox/Facts tab is their
    // home. (Cardinality is the `uniqueness_key` knob now, ADR-0005 §2.)
    if (hasObjectUniquenessKey(decl)) continue;
    const kind = decl.value_kind[0] ?? 'text';
    if (kind === 'enum') {
      const text = formatSummaryValue(currentFactValue(fact));
      if (text !== null && text.length <= DESCRIPTOR_MAX_LEN) enumParts.push(text);
      continue;
    }
    if (kind === 'text' || kind === 'number') {
      const text = formatSummaryValue(currentFactValue(fact));
      if (text !== null && text.length <= DESCRIPTOR_MAX_LEN) textParts.push(text);
    }
    // ref / rich_text / asset / image / file / date / time / boolean / tuple
    // are intentionally not descriptors.
  }
  // Enums first (cleanest subtype signal), then short text/number, capped.
  return [...enumParts, ...textParts].slice(0, DESCRIPTOR_MAX_COUNT).join(' · ');
}

/**
 * The row meta line: the localised root label (the floor), then the curated
 * `featured` summary when present, else the cheap scalar descriptor fallback
 * (#B). Always non-empty.
 *
 * Exported so the command palette (`app.ts viewPalette`) renders the SAME
 * enriched descriptor on its result rows — the meta line was previously just
 * the bare root label there too. Cheap enough for per-keystroke palette
 * rendering (folded-entity + in-memory-registry reads only — see
 * `scalarDescriptor`). The caller passes the already-localised `rootLabel`.
 */
export function entityMetaLine(entity: EntityState, rootLabel: string): string {
  return rowMeta(entity, rootLabel);
}

function rowMeta(entity: EntityState, rootLabel: string): string {
  const featured = featuredSummary(entity);
  if (featured) return `${rootLabel} · ${featured}`;
  const descriptor = scalarDescriptor(entity);
  return descriptor ? `${rootLabel} · ${descriptor}` : rootLabel;
}

/**
 * The predicate an entity's title is read from — the built-in `name` system
 * predicate (ADR-0004 §2, #173). References the `@dl/shared` code constant
 * rather than a bare `'name'` string literal so title rendering stays bound
 * to the single grandfathered system token even after core.json is removed
 * (#183). Present in every campaign without any pack subscription.
 */
const FACT_NAME_PREDICATE = BUILTIN_NAME_PREDICATE_ID;
/**
 * Extract the display name from an EntityState. The convention is the
 * `name` predicate's current value; entities without a name fact fall
 * back to their id so they remain visible in the list. Exported so
 * the entity page (and any future surface that titles an entity) can
 * share the same resolution rule.
 */
export function displayName(entity: EntityState): string {
  const fact = entity.facts[FACT_NAME_PREDICATE];
  if (!fact) return entity.id;
  const latest = fact.versions[fact.versions.length - 1];
  if (latest === undefined) return entity.id;
  const v = latest.value;
  return v === undefined || v === null ? entity.id : String(v);
}

/**
 * Pull the rows for one root out of the model's entities bag, NFD-
 * sorted alphabetically. Same-module helper — the plugin walker
 * resolves the call site through this.
 */
function rowsForRoot(entities: EntitiesBag, root: EntityRoot): EntityRow[] {
  // The meta line leads with the localised root label (the floor) and
  // appends the curated featured-fact summary when present — mirroring
  // the mock's `Location · city · capital` `.result-meta` treatment.
  const rootLabel = t(`entity.root.${root}`);
  const rows: EntityRow[] = [];
  for (const entity of Object.values(entities)) {
    if (entity.root !== root) continue;
    if (entity.soft_deleted) continue;
    const name = displayName(entity);
    rows.push({
      id: entity.id,
      name,
      sortKey: normalizeName(name),
      meta: rowMeta(entity, rootLabel),
    });
  }
  rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return rows;
}

/**
 * Public entry. Renders six root sections in canonical order; opens
 * the Locations section by default (Wave 1 muscle-memory — the only
 * list before this ticket was Locations).
 *
 * The whole tree is built inline so every reactive accessor closes
 * over `entitiesAccessor` (a function reference) — and the consumer
 * passes a same-module `const` for that accessor, so the v2c walker
 * resolves it statically through the module-level lookup.
 */
export function viewMultiRootEntityList<Model extends EntityListStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  nav: EntityListNav,
  /**
   * When set (driven by the left-rail Browse links / `/browse/:root` route),
   * render ONLY this root's section, expanded. `null` → the full six-section
   * accordion (bare `/`). The list arm in the host keys its remount on the
   * active root, so this is read once per mount (not reactively).
   */
  activeRoot: EntityRoot | null = null,
): Mountable {
  const roots = activeRoot === null ? ENTITY_ROOTS : [activeRoot];
  // Same-module accessors close over `root` (a literal `EntityRoot`
  // value pulled from `ENTITY_ROOTS`). Each accessor delegates to
  // bare-Identifier same-module function declarations (`pickRowsForRoot`,
  // `pickCountForRoot`) so the v0.5.x cross-file walker resolves them
  // statically — no function-parameter callees in the reactive path.
  const sections = roots.map((root) => {
    // Precompute the localised root label so reactive accessors don't
    // close over a `t(`entity.root.${root}`)` call — the cross-file
    // walker treats `t` (imported from `../i18n.js`) as opaque even
    // when the argument isn't `s`.
    const rootLabel = t(`entity.root.${root}`);
    // Single-root filter → that section opens; otherwise Locations opens
    // by default (Wave 1 muscle memory).
    const isOpenByDefault = activeRoot !== null || root === 'location';

    // #N002 — wrap each section in a flex row so the per-section
    // "+ New <root>" create button sits next to the disclosure (NOT
    // inside the summary). Nesting a <button> inside a <summary>
    // triggers axe's nested-interactive rule because <summary>
    // already has the implicit role of "button".
    //
    // The section itself is the @dl/ui/collapse atom — native
    // <details>/<summary> with design-system chrome. The per-root
    // testid + data-root attrs are forwarded via dataAttrs so e2e
    // selectors keep working.
    return div({ class: 'entity-root-section-row' }, [
    collapse({
      openByDefault: isOpenByDefault,
      className: 'entity-root-section',
      summaryClass: 'entity-root-summary',
      testid: 'entity-root-section',
      dataAttrs: { 'data-root': root },
      summary: [
        span(
          {
            class: 'entity-root-chip',
            'data-root': root,
            'aria-hidden': 'true',
          },
          // §5 — the real Lucide per-root icon (replaces the geometric
          // glyph). Decorative; the localised root label sits beside it
          // so colour/shape is never the only signal (#86, §10.8).
          [entityIcon(root, { size: 16 })],
        ),
        span({ class: 'entity-root-label' }, [
          text(rootLabel),
        ]),
        badge({
          tone: 'neutral',
          testid: 'entity-root-count',
          attrs: {
            // The aria-label announces context with the count so screen
            // readers hear "Characters: 12 entities" rather than the
            // raw number alone.
            'aria-label': state.map((s) =>
              t('entities.list.count_label', {
                root: rootLabel,
                n: pickCountForRoot(s, root),
              }),
            ),
          },
          children: [text(state.map((s) => String(pickCountForRoot(s, root))))],
        }),
      ],
      children: [
        show(
          state.map((s) => pickCountForRoot(s, root) === 0),
          () => [
            emptyState({
              testid: 'entity-root-empty',
              attrs: { 'data-root': root },
              message: text(t(`entities.empty.${root}`)),
            }),
          ],
        ),
        show(
          state.map((s) => pickCountForRoot(s, root) > 0),
          () => [
            ul(
              {
                class: 'entity-list entity-root-list',
                'data-testid': 'entity-root-list',
                'data-root': root,
              },
              [
                each(
                  state.map((s) => pickRowsForRoot(s, root)),
                  {
                    key: (row) => row.id,
                    // §8.2 / mock — the clean icon-tiled entityCard row
                    // (replaces the bordered name-only box): per-root
                    // Lucide icon tile + default sans name + a mono meta line
                    // leading with the root label. Keeps the legacy
                    // `entity-row` / `entity-row-link` testids the e2e
                    // suite drives.
                    render: (item) => [
                      entityCard({
                        as: 'li',
                        root,
                        testid: 'entity-row',
                        dataRoot: root,
                        name: text(item.at('name')),
                        meta: text(item.at('meta')),
                        href: item.at('id').map((id) => nav.href(id)),
                        nameAttrs: { 'data-testid': 'entity-row-link' },
                        onNameClick: (event: MouseEvent) => {
                          event.preventDefault();
                          const id = item.at('id').peek();
                          if (typeof id !== 'string') return;
                          send(nav.navigateMsg(id) as Msg);
                        },
                      }),
                    ],
                  },
                ),
              ],
            ),
          ],
        ),
      ],
    }),
    // #N002 — per-section "+" create button. Sits as a SIBLING of
    // `<details>`, not inside `<summary>`, to avoid axe's
    // `nested-interactive` rule (summary already has role=button). The
    // Location section keeps the legacy `data-testid="new-location"`
    // anchor so existing Playwright tests keep passing; other roots
    // get `new-<root>` ids.
    uiButton({
      variant: 'icon',
      testid: root === 'location' ? 'new-location' : `new-${root}`,
      ariaLabel: t('entities.list.create_label', { root: rootLabel }),
      onClick: () => send(nav.createForRootMsg(root) as Msg),
      className: 'entity-root-create',
      attrs: { 'data-root': root },
      // Real Lucide `plus` rather than the '+' text character, which rendered
      // in the body font at a different weight and optical size than every
      // other icon on the page.
      children: [plusIcon({ size: 16 })],
    }),
    ]);
  });

  return div(
    {
      class: 'entity-multi-root-list',
      'data-testid': 'entity-multi-root-list',
      role: 'region',
      'aria-label': t('entities.list.region_label'),
    },
    sections,
  );
}
